import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { BookOpen, Clock, CheckCircle, Eye } from 'lucide-react';
import { LevelBadge, statusLabels, statusColors, priorityLabels, priorityColors, diffStatusLabels, diffStatusColors } from './StudentBadges';

export const MasteryTable = ({ mastery }) => (
        <Card className="bg-card border-border" data-testid="mastery-full-table">
          <CardContent className="p-0">
            {mastery.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Notion</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Score</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Statut</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Questions</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">QCM</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Priorité</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mastery.map(m => (
                    <TableRow key={m.notion_id} className="hover:bg-muted/30" data-testid={`student-mastery-${m.notion_id}`}>
                      <TableCell>
                        <p className="text-sm font-medium">{m.notion_label}</p>
                        {(m.theme || m.chapter) && <p className="text-xs text-muted-foreground">{[m.theme, m.chapter].filter(Boolean).join(' › ')}</p>}
                      </TableCell>
                      <TableCell><LevelBadge value={m.score} /></TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[m.status] || ''}`}>
                          {statusLabels[m.status] || m.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{m.total}</TableCell>
                      <TableCell className="text-sm">{m.quiz_count}</TableCell>
                      <TableCell>
                        {m.priority ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColors[m.priority] || ''}`}>
                            {priorityLabels[m.priority] || m.priority}
                          </span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
                <p className="text-muted-foreground">Aucune notion taguée sur les questions des QCM passés.</p>
              </div>
            )}
          </CardContent>
        </Card>
);

export const HistoryTable = ({ history }) => (
        <Card className="bg-card border-border" data-testid="history-full-table">
          <CardContent className="p-0">
            {history.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">QCM</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Type</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Score</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Médiane classe</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Écart</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Notions fragiles</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map(h => (
                    <TableRow key={h.result_id} className="hover:bg-muted/30" data-testid={`history-row-${h.result_id}`}>
                      <TableCell className="text-sm">{h.date ? new Date(h.date).toLocaleDateString('fr-FR') : '—'}</TableCell>
                      <TableCell className="font-medium text-sm">{h.quiz_title}</TableCell>
                      <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{h.quiz_type}</span></TableCell>
                      <TableCell>
                        <LevelBadge value={h.student_score} />
                        <span className="text-xs text-muted-foreground ml-1">({h.student_score_raw})</span>
                      </TableCell>
                      <TableCell><LevelBadge value={h.class_median} /></TableCell>
                      <TableCell>
                        {h.diff_from_median != null ? (
                          <span className={`text-sm font-semibold ${h.diff_from_median >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {h.diff_from_median >= 0 ? '+' : ''}{h.diff_from_median}
                          </span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {h.fragile_notions?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {h.fragile_notions.map((n, i) => (
                              <span key={`${n}-${i}`} className="text-xs px-1.5 py-0.5 rounded bg-red-400/10 text-red-400">{n}</span>
                            ))}
                          </div>
                        ) : <span className="text-xs text-emerald-400">Aucune</span>}
                      </TableCell>
                      <TableCell>
                        <Link to={`/results?quiz=${h.quiz_id}`}>
                          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary"><Eye className="w-3.5 h-3.5" /></Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
                <p className="text-muted-foreground">Aucun QCM passé par cet élève.</p>
              </div>
            )}
          </CardContent>
        </Card>
);

export const DifficultiesTable = ({ difficulties }) => (
        <Card className="bg-card border-border" data-testid="difficulties-table">
          <CardContent className="p-0">
            {difficulties.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Notion</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Score</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Questions</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">QCM</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {difficulties.map(d => (
                    <TableRow key={d.notion_id} className="hover:bg-muted/30" data-testid={`difficulty-row-${d.notion_id}`}>
                      <TableCell>
                        <p className="text-sm font-medium">{d.notion_label}</p>
                        {(d.theme || d.chapter) && <p className="text-xs text-muted-foreground">{[d.theme, d.chapter].filter(Boolean).join(' › ')}</p>}
                      </TableCell>
                      <TableCell><LevelBadge value={d.score} /></TableCell>
                      <TableCell className="text-sm">{d.total}</TableCell>
                      <TableCell className="text-sm">{d.quiz_count}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${diffStatusColors[d.difficulty_status] || ''}`}>
                          {diffStatusLabels[d.difficulty_status] || d.difficulty_status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-400" strokeWidth={1} />
                <p className="text-muted-foreground">Aucune difficulté récurrente détectée.</p>
                <p className="text-xs text-muted-foreground mt-1">Une difficulté est récurrente quand le score reste &lt;50% sur au moins 6 questions et 3 QCM.</p>
              </div>
            )}
          </CardContent>
        </Card>
);
